// How a multi-series brand chart distributes colour and weight.
//
// Emphasis used to be decided by array position: series[0] got a thick solid accent and
// everything after it a thin dashed line. Combined with a brand-colour rule that mapped
// EVERY own brand to the same accent, two own brands (XQS · ACE) came out the same colour,
// one of them thin — indistinguishable, with the smaller one hugging zero (C23).
//
// Emphasis now follows ownership, not order: own brands are thick, solid and each in its
// own saturated colour; competitors are thin, dashed and share one neutral baseline so the
// own series read first (the DS rule — own pops, others are one reference tone). Pure so
// the rule is unit-tested without a chart or a tenant.

export interface SeriesStyle {
  key: string
  own: boolean
  color: string
  width: number
  dash?: string
  opacity: number
  dot: boolean
}

// Competitor dash patterns, so a capped handful stay tellable apart while all sharing the
// neutral tone.
const COMPETITOR_DASH = ['5 4', '2 3', '8 3 2 3']

const norm = (s: string): string => s.trim().toUpperCase()

/**
 * @param seriesKeys the plotted series, in chart order
 * @param ownBrands  the viewer's own brands (order fixes each one's colour)
 * @param palette    DS series colours, chart-1 → chart-4. Last entry is the neutral tone.
 */
export function seriesStyles(
  seriesKeys: readonly string[],
  ownBrands: readonly string[],
  palette: readonly string[],
): SeriesStyle[] {
  const ownOrder = [...new Set(ownBrands.map(norm))].filter(Boolean)
  const saturated = palette.slice(0, Math.max(1, palette.length - 1))
  const neutral = palette[palette.length - 1] ?? palette[0]
  // With no own brands (the whole-market profile) there is nothing to emphasise, so every
  // series stays distinguishable by colour instead of collapsing to one neutral tone.
  const anyOwn = seriesKeys.some((k) => ownOrder.includes(norm(k)))

  let competitorSeen = 0
  return seriesKeys.map((key, i) => {
    const ownIdx = ownOrder.indexOf(norm(key))
    const own = ownIdx >= 0
    if (own) {
      return {
        key,
        own: true,
        color: saturated[ownIdx % saturated.length],
        width: 2.5,
        opacity: 1,
        dot: true,
      }
    }
    if (!anyOwn) {
      return {
        key,
        own: false,
        color: palette[i % palette.length],
        width: 2,
        opacity: 1,
        dot: false,
      }
    }
    const dash = COMPETITOR_DASH[competitorSeen % COMPETITOR_DASH.length]
    competitorSeen += 1
    return { key, own: false, color: neutral, width: 1.5, dash, opacity: 0.8, dot: false }
  })
}
